import { useEffect, useRef, RefObject } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';

import { loginCodeAtom, loginRequestAtom } from '_atoms/auth';
import { loginActionAtom } from '_atoms/loginAction';
import { getLoginId } from '_helpers/loginHelpers';

/**
 * Hook that initializes the login session on the first app load.
 *
 * Behavior:
 * - If a login ID exists in session storage or cookies, rehydrate the app state via the API.
 * - If a login code/custom ID exists in the URL, post a new login request.
 * - Ensures this runs only once per app load.
 */
export const useSession = () => {
  const loginCode = useAtomValue(loginCodeAtom);
  const login = useSetAtom(loginActionAtom);
  const setLoginRequest = useSetAtom(loginRequestAtom);

  const searchParams: URLSearchParams = useSearchParams()[0];

  // Capture initial search params and run-flag so they don't change on re-renders
  const refSearchParams: RefObject<URLSearchParams> = useRef(searchParams);
  const hasRunRef = useRef(false);

  useEffect(() => {
    // Exit early if this hook has already run or the user is already logged in
    if (hasRunRef.current || loginCode.id) return;
    hasRunRef.current = true;

    const loginId: string | null = getLoginId();
    const loginCodeFromURL: string | null = refSearchParams.current.get('loginCode');
    const customId: string | null = refSearchParams.current.get('customId');

    // Exit if there’s no valid login method
    if (!loginId && !loginCodeFromURL) return;

    // Seed the login request atom with initial values
    setLoginRequest({
      backupOneTimePassword: null,
      customIdValue: customId,
      loginCodeValue: loginCodeFromURL,
      method: 'C',
      password: null,
      oneTimePassword: null
    });

    // Attempt login
    void login(loginId);

    // Clean up URL params after use
    refSearchParams.current.delete('loginCode');
    refSearchParams.current.delete('customId');
  }, [loginCode.id, login, setLoginRequest]);
}